import { useMemo } from 'react';
import isEqual from 'lodash/isEqual';

import type { PPGRow } from '../../../../types/promoMetrics';
import type { PromoFieldConfig } from '../../../../config/tpoGenConfig';
import { normalizeValue, normalizeRecord } from '../../../../utils/formNormalize';
import { areRequiredFieldsFilled } from '../../utils/promoStateUtils';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';

/** True if the field contributes a unit value to the persisted offer payload. */
const offerMappingHasUnit = (f: PromoFieldConfig): boolean => {
    if (!f.offerMapping) return false;
    const mappings = Array.isArray(f.offerMapping) ? f.offerMapping : [f.offerMapping];
    return mappings.some((m) => !!m.unitParamName);
};

interface UsePromoDirtyStateArgs {
    isEditMode: boolean;
    ppgData: PPGRow[];
    baseline: Map<string, Record<string, number>>;
    promoFormValues: Record<string, unknown>;
    existingPromoFormValues: Record<string, unknown> | null;
    existingPpgIds: number[];
    hasProducts: boolean;
    isLoadingRecommendedValues: boolean;
    isFetchingPromoData: boolean;
    isFormHydrated: boolean;
    hasFormErrors: boolean;
    isUpliftPending: boolean;
    /** Engine-derived promo field states (drives the required-fields save gate). */
    fieldStates: Record<string, { visible: boolean; disabled: boolean; required: boolean }>;
}

interface UsePromoDirtyStateReturn {
    isDirty: boolean;
    canSave: boolean;
}

const usePromoDirtyState = ({
    isEditMode,
    ppgData,
    baseline,
    promoFormValues,
    existingPromoFormValues,
    existingPpgIds,
    hasProducts,
    isLoadingRecommendedValues,
    isFetchingPromoData,
    isFormHydrated,
    hasFormErrors,
    isUpliftPending,
    fieldStates,
}: UsePromoDirtyStateArgs): UsePromoDirtyStateReturn => {
    const { promoFields } = useTpoGenConfig();

    const apiSyncedFieldNames = useMemo(
        () => new Set(promoFields.filter((f) => !f.hidden && (f.responseKey !== null || f.offerMapping)).map((f) => f.name)),
        [promoFields],
    );

    // Config-visible field names only (drops hidden unit-toggle / constant fields);
    // used by the create-flow dirty check so seeded/hidden values don't mark it dirty.
    const editableFieldNames = useMemo(() => promoFields.filter((f) => !f.hidden).map((f) => f.name), [promoFields]);

    // Unit-toggle fields always carry a default (e.g. 'abs') in the form even for offers that don't
    // use them, while the API baseline only stores the unit of the saved offer. Compare a unit only
    // when (a) it is actually persisted to the API and (b) its owning offer field has a value — so a
    // leftover default on an unused or non-persisted offer never marks the form dirty.
    const unitFieldOwners = useMemo(
        () =>
            promoFields
                .flatMap((f) =>
                    f.unitToggle && offerMappingHasUnit(f)
                        ? [{ unitField: f.unitToggle.unitField, ownerField: f.name }]
                        : [],
                )
                .filter((entry, idx, arr) => arr.findIndex((e) => e.unitField === entry.unitField) === idx),
        [promoFields],
    );

    const hasMetricChanges = useMemo(() => {
        return ppgData.some((ppg) => {
            const original = baseline.get(String(ppg.ppg_id));
            if (!original) return true;
            return Object.entries(ppg.values).some(([key, val]) => (original[key] ?? null) !== val);
        });
    }, [ppgData, baseline]);

    const normalizedBaseline = useMemo(
        () => (existingPromoFormValues ? normalizeRecord(existingPromoFormValues) : null),
        [existingPromoFormValues],
    );

    const hasPromoFormChanges = useMemo(() => {
        if (!normalizedBaseline) return false;
        for (const key of apiSyncedFieldNames) {
            if (fieldStates[key]?.disabled || fieldStates[key]?.visible === false) continue;
            const baselineVal = normalizedBaseline[key] ?? null;
            const current = normalizeValue(promoFormValues[key]);
            if (!isEqual(baselineVal, current)) return true;
        }
        for (const { unitField, ownerField } of unitFieldOwners) {
            if (fieldStates[unitField]?.disabled || fieldStates[unitField]?.visible === false) continue;
            const ownerHasValue =
                normalizeValue(promoFormValues[ownerField]) !== null ||
                (normalizedBaseline[ownerField] ?? null) !== null;
            if (!ownerHasValue) continue;
            const baselineVal = normalizedBaseline[unitField] ?? null;
            const current = normalizeValue(promoFormValues[unitField]);
            if (!isEqual(baselineVal, current)) return true;
        }
        return false;
    }, [promoFormValues, normalizedBaseline, apiSyncedFieldNames, unitFieldOwners, fieldStates]);

    const hasProductChanges = useMemo(() => {
        if (!isEditMode || existingPpgIds.length === 0) return false;
        const currentIds = new Set(ppgData.map((p) => p.ppg_id));
        const existingIds = new Set(existingPpgIds);
        for (const id of currentIds) {
            if (!existingIds.has(id)) return true;
        }
        for (const id of existingIds) {
            if (!currentIds.has(id)) return true;
        }
        return false;
    }, [isEditMode, ppgData, existingPpgIds]);

    const isDirty = useMemo(() => {
        if (isEditMode) {
            return hasPromoFormChanges || hasMetricChanges || hasProductChanges;
        }
        // Create flow: dirty only if the user filled a field that's actually editable —
        // ignore disabled (mirrored/derived) and hidden fields, which are seeded programmatically.
        return editableFieldNames.some((key) => {
            const state = fieldStates[key];
            if (state?.disabled || state?.visible === false) return false;
            const v = promoFormValues[key];
            return v !== undefined && v !== null && v !== '';
        });
    }, [
        isEditMode,
        hasPromoFormChanges,
        hasMetricChanges,
        hasProductChanges,
        editableFieldNames,
        fieldStates,
        promoFormValues,
    ]);

    const canSave = useMemo(() => {
        if (hasFormErrors || isUpliftPending || isFetchingPromoData) return false;
        if (isEditMode) {
            if (!isFormHydrated) return false;
            return (hasPromoFormChanges || hasMetricChanges || hasProductChanges) && !isLoadingRecommendedValues;
        }
        return (
            areRequiredFieldsFilled(fieldStates, promoFormValues) &&
            hasProducts &&
            ppgData.length > 0 &&
            !isLoadingRecommendedValues
        );
    }, [
        hasFormErrors,
        isUpliftPending,
        isFetchingPromoData,
        isEditMode,
        isFormHydrated,
        hasPromoFormChanges,
        hasMetricChanges,
        hasProductChanges,
        fieldStates,
        promoFormValues,
        isLoadingRecommendedValues,
        hasProducts,
        ppgData.length,
    ]);

    return { isDirty, canSave };
};

export default usePromoDirtyState;
